import React from 'react';
import { Link } from 'react-router-dom';
import {
  LuMapPin, LuPlane, LuMap, LuTarget, LuBuilding2, LuBanknote,
  LuDollarSign, LuEuro, LuJapaneseYen, LuPoundSterling, LuCoins
} from 'react-icons/lu';
import { PersonRenderer, iconCell, valueIconCell } from 'components/common/ag-grid';

// Type of trip: the mark follows the value — domestic stays on the map,
// international takes the plane.
const TRIP_TYPE_ICONS = {
  domestic:      { icon: LuMap,   className: 'tw-text-emerald-500' },
  international: { icon: LuPlane, className: 'tw-text-sky-500' },
};

// Currency: glyph per code; anything unmapped falls back to generic coins.
const CURRENCY_ICONS = {
  USD: { icon: LuDollarSign,    className: 'tw-text-emerald-600' },
  EUR: { icon: LuEuro,          className: 'tw-text-indigo-500' },
  JPY: { icon: LuJapaneseYen,   className: 'tw-text-rose-500' },
  GBP: { icon: LuPoundSterling, className: 'tw-text-violet-500' },
  VND: { icon: LuBanknote,      className: 'tw-text-amber-600' },
};
import BookingDateFormatRenderer from "./BookingDateFormatRenderer";
import BookingStatusBadge from "./BookingStatusBadge";
import BookingEmployeeCodeBadgeRenderer from "./BookingEmployeeCodeBadgeRenderer";
import { formatNumber } from "helpers/utils";
import BookingWorkflowRenderer from "./BookingWorkflowRenderer";
import BookingLinkRenderer from "./BookingLinkRenderer";
import BookingEmployeeNameBadgeRenderer from "./BookingEmployeeNameBadgeRenderer";

export const columnDefs = [
  { 
    headerName: 'ID#',
    field: 'booking_number',
    minWidth: 100,
    width: 120,
    flex: 0,
    pinned: 'left',
    cellRenderer: BookingLinkRenderer
  },
  { 
    headerName: 'Trip name',
    field: 'trip_name',
    minWidth: 200,
    cellRenderer: BookingLinkRenderer
  },
  { 
    headerName: 'Destination',
    field: 'destination',
    minWidth: 200,
    cellRenderer: iconCell(LuMapPin)
  },
  { 
    headerName: 'Date of travel',
    field: 'date_of_travel', 
    minWidth: 170,
    cellRenderer: BookingDateFormatRenderer
  },
  { 
    headerName: 'Date of return',
    field: 'date_of_return',
    minWidth: 170,
    cellRenderer: BookingDateFormatRenderer
  },
  { 
    headerName: 'Workflow',
    field: 'workflow_message',
    minWidth: 250,
    cellRenderer: BookingWorkflowRenderer
  },
  { 
    headerName: 'Type of trip',
    field: 'type_of_travel',
    minWidth: 110,
    cellRenderer: valueIconCell((v) => TRIP_TYPE_ICONS[String(v).toLowerCase()])
  },
  {
    headerName: 'Purpose of travel',
    field: 'purpose_of_travel',
    minWidth: 180,
    cellRenderer: iconCell(LuTarget)
  },
  { 
    headerName: 'Description purpose of travel',
    field: 'description_purpose_of_travel',
    minWidth: 250,
  },
  { 
    headerName: 'Status',
    field: 'status.name',
    minWidth: 150,
    cellRenderer: BookingStatusBadge
  },
  { 
    headerName: 'Job number',
    field: 'job_number',
    minWidth: 130,
    cellRenderer: (params) => {
      if (!params.value) return null;
      const projectId = params.data?.project?.project_id;
      if (!projectId) return <span>{params.value}</span>;
      return (
        <Link to={`/crm/projects/${projectId}`} target="_blank">
          {params.value}
        </Link>
      );
    },
  },
  {
    headerName: 'Budget amount',
    field: 'project.budget_amount_vnd',
    minWidth: 150,
    valueFormatter: (p) => (p.value ? `${formatNumber(p.value, 'VND')} VND` : ''),
  },
  {
    headerName: 'Budget remaining',
    field: 'project.budget_remaining_vnd',
    minWidth: 150,
    valueFormatter: (p) => (p.value != null ? `${formatNumber(p.value, 'VND')} VND` : ''),
  },
  { 
    headerName: 'Site',
    field: 'site_name',
    minWidth: 130,
    cellRenderer: iconCell(LuBuilding2)
  },
  { 
    headerName: 'Employees',
    field: 'employees_name',
    minWidth: 200,
    cellRenderer: BookingEmployeeNameBadgeRenderer
  },
  { 
    headerName: 'Employees code',
    field: 'employees_code',
    minWidth: 200,
    cellRenderer: BookingEmployeeCodeBadgeRenderer
  },
  { 
    headerName: 'Currency',
    field: 'currency',
    minWidth: 100,
    cellRenderer: valueIconCell((v) => CURRENCY_ICONS[String(v).toUpperCase()] || { icon: LuCoins })
  },
  {
    headerName: 'Created by',
    field: 'submited_by',
    minWidth: 120,
    cellRenderer: PersonRenderer
  },
  { 
    headerName: 'Created date',
    field: 'submited_date',
    minWidth: 150
  }
];